/**
 * Masse des Stadions, angelehnt ans Berliner Olympiastadion — ein
 * Erdstadion: der Unterring liegt in der Grube, die Umgaenge zwischen den
 * Raengen auf Gelaendehoehe, der Oberring darueber. Aussen laeuft auf
 * Gelaendehoehe der Aussenring — der Pfeilergang des Vorbilds — mit den
 * Treppentuermen in den Oberring, den Tunneln (Mundloechern) hinunter zur
 * Promenade und den Imbissen. Vom Unterring fuehrt kein Weg nach oben; wer
 * hinauf will, geht durch den Tunnel zurueck in den Aussenring. Eine Kachel
 * ist ein Meter; die Laufbahn ist die Kurve, an der sich alles ausrichtet.
 *
 * Der Weg des Vorbilds von 1936, den die Baender nachzeichnen: „Ebenerdig
 * erreicht der Zuschauerstrom die mittlere Hoehe des Stufenbaues […] Rings
 * um das Stadion verteilt fuehren Durchgaenge in strahlenfoermiger
 * Anordnung ueber einen inneren Umgang die Zuschauer zu den einzelnen
 * Abschnitten des Unterrings." Darum liegt zwischen Promenade und Reihen
 * der Pfeilerkranz mit seinen Durchlaessen und dahinter der Rundgang.
 */
export const STADION_BREITE = 292;
export const STADION_HOEHE = 220;
export const MITTE_X = STADION_BREITE / 2;
export const MITTE_Y = STADION_HOEHE / 2;
/** Halbe Laenge der Geraden der Laufbahn (Kacheln). */
export const GERADE_HALB = 36;
/** Radius der Innenkante der Laufbahn um die Endpunkte der Geraden. */
export const BAHN_RADIUS = 31;
/** Spielfeld: Rechteck um die Mitte, halbe Masse. */
export const FELD_HALB_X = 45;
export const FELD_HALB_Y = 28;
/**
 * Baender nach Abstand `d` von der Bahn-Innenkante (Kacheln), Anfang je
 * Band. Von innen nach aussen: Laufbahn, Bande, schmaler Innenumgang,
 * 24 Reihen Unterring, der Rundgang oben am Unterring, der Pfeilerkranz
 * mit den Mundloechern, die Promenade, das Sockelband, 21 Reihen
 * Oberring, der obere Umgang (der aeussere Pfeilergang des Vorbilds), die
 * Rueckwand, der Aussenring und die Arkade.
 */
export const BAND = {
    tartan: 0,
    innenBruestung: 8,
    innenUmgang: 9,
    unterring: 10,
    /** Der Rundgang oben im Unterring: von hier steigt man in die Bloecke. */
    rundgang: 34,
    /** Der Pfeilerkranz: Wand mit Durchlaessen auf jeder Unterring-Treppe. */
    pfeilerkranz: 37,
    promenade: 38,
    sockel: 40,
    oberring: 42,
    oberUmgang: 63,
    fassade: 65,
    /** Der Aussenring: Treppentuerme, Tunnelmuender, Imbisse. */
    aussenring: 66,
    /** Die Arkade: freier Gang unter den Pfeilern, immer begehbar. */
    arkade: 76,
    ende: 78,
};
export const UNTERRING_REIHEN = BAND.rundgang - BAND.unterring; // 24
export const OBERRING_REIHEN = BAND.oberUmgang - BAND.oberring; // 21
/** Hoehen in Metern. */
export const HOEHE_INNEN_UMGANG = 0.6;
export const HOEHE_UNTERRING_START = 1.0;
/** Hoehe je Reihe und je Treppenstufe — unter MAX_STUFE_M. */
export const STUFE = 0.5;
/** Gelaendehoehe: Rundgang, Promenade, Aussenring, Marathontor. */
export const HOEHE_GELAENDE = 13.0;
/** Der Pfeilerkranz zwischen Rundgang und Promenade — drei Meter hoch. */
export const HOEHE_PFEILERKRANZ = 16.0;
/** Das Sockelband des Oberrings — eine drei Meter hohe Wand zur Promenade, ohne Aufgang. */
export const HOEHE_SOCKEL = 16.0;
export const HOEHE_OBERRING_START = 16.5;
export const HOEHE_OBER_UMGANG = 27.0;
/** Schale der Treppentuerme, knapp ueber dem oberen Umgang. */
export const HOEHE_TURMSCHALE = 28.0;
/** Wange zwischen zwei Treppenlaeufen ragt so weit ueber den hoeheren hinaus. */
export const WANGE_UEBERSTAND = 0.9;
/**
 * Treppen des Unterrings: Geraden alle 12 m, Kurven als Strahlen alle 17 Grad.
 * Auf jeder zweiten dieser Achsen liegen aussen die Tore — aus dem Mundloch
 * geht es geradeaus ueber die Promenade, durch den Pfeilerkranz auf den
 * Rundgang und dort auf die Treppe hinunter.
 */
export const TREPPEN_ABSTAND_GERADE = 12;
export const TREPPEN_GERADE_ANZAHL_HALB = 2;
export const TREPPEN_WINKEL_GRAD = [0, 17, 34, 51, 68, 85];
/** Treppen des Oberrings: jeweils mittig zwischen zwei Toren. */
export const OBERRING_TREPPEN_WINKEL_GRAD = [17, 51, 85];
export const OBERRING_TREPPEN_GERADE_X = [0, 24];
/** Halbe Breite der Treppe (Kacheln); 1,2 laesst Radius 0,35 auch schraeg durch. */
export const TREPPE_HALBBREITE = 1.2;
/**
 * Halbe Breite der Durchlaesse im Pfeilerkranz. Etwas weiter als die
 * Treppe: man tritt aus dem Mundloch heraus und hat den Lauf vor sich,
 * nicht neben sich.
 */
export const DURCHLASS_HALBBREITE = 1.6;
/** Marathontor: Plattform in der Westkurve, halbe Breite quer zur Achse. */
export const MARATHONTOR_HALBBREITE = 11;
/**
 * Zugaenge: radiale Achsen, an denen Tore, Tuerme und Imbisse haengen. In
 * den Kurven Strahlen vom Kurvenmittelpunkt (Grad zur Achse des Bogens,
 * beidseitig), auf den Geraden senkrechte Linien (Abstand von der Mitte).
 * Quer zur Achse zaehlt `v` in Umlaufrichtung Osten → Sueden → Westen →
 * Norden — so liegt der Turm jedes Tors im Sektor *nach* dem Tor, und jeder
 * Sektor des Oberrings hat seinen Aufgang.
 *
 * Tore: 13 Tunnel auf Gelaendehoehe vom Aussenring durch den Oberring bis
 * zur Promenade, jeweils auf einer Unterring-Treppe; das Osttor liegt auf
 * der Ostachse, im Westen ist das Marathontor der Zugang. Halbe Breite 1,5
 * ergibt vier Kacheln, also vier Meter.
 */
export const TORE_WINKEL_OST_GRAD = [0, 34, 68];
export const TORE_WINKEL_WEST_GRAD = [34, 68];
export const TORE_GERADE_X = [12];
export const TOR_HALBBREITE = 1.5;
/** Neben dem Tunnel eine Kachel Gelaender — dort geht es nicht hinunter. */
export const TOR_RAND = 1.0;
/**
 * Treppentuerme im Aussenring: sechzehn Spalten (Schale, fuenf Laeufe zu je
 * zwei Kacheln, vier Wangen, Schale) neben jedem Tor, Laeufe ueber fuenf
 * Zeilen, Podeste zwei Zeilen tief, je Lauf samt Podeststufe knapp drei
 * Meter Hub — vom Aussenring bis zum oberen Umgang. Zwei Kacheln breit
 * muessen Laeufe wie Podeste sein: in den Kurven liegen sie schraeg im
 * Raster, und eine einspurige Diagonale ist nur ueber Ecken verbunden.
 * Zwei weitere Tuerme flankieren das Marathontor, dessen Sektor sonst
 * keinen Aufgang haette — die Tuerme des Vorbilds stehen genau dort.
 *
 * Das Tor in den oberen Umgang ist genau der aeussere Lauf, zwei Kacheln
 * breit. Breiter geht im Kachelraster nicht: die Wangenspalte liegt in den
 * Kurven orthogonal neben dem Podest des vorletzten Laufs. Auffindbar macht
 * das Theme es mit einem Portalrahmen.
 */
export const TURM = {
    /** Erste Turmspalte liegt so weit neben der Achse (Kacheln), hinter dem Tunnelrand. */
    abstandVonAchse: 3,
    breite: 16,
    fluchten: 5,
    laufBreite: 2,
    fluchtLaenge: 5,
    hubJeFlucht: 2.8,
    podestTiefe: 2,
    /** Zeilen des Turms in Abstand von der Bahn (Podeste: erste Zeile). */
    torZeile: 65,
    innenPodest: 66,
    erstesLaufFeld: 68,
    aussenPodest: 73,
    /** Aeussere Schale mit der Tuer zum Aussenring. */
    tuerZeile: 75,
};
export const MARATHONTOR_TUERME_GRAD = 7;
/**
 * Imbisse: gesperrte Buden im Aussenring, vier breit und drei tief, zwischen
 * den Tuermen. Im Westen ruecken sie hinter die Marathontor-Tuerme.
 */
export const IMBISSE_WINKEL_OST_GRAD = [17, 51];
export const IMBISSE_WINKEL_WEST_GRAD = [25, 51];
export const IMBISSE_GERADE_X = [33];
export const IMBISS_HALBBREITE = 1.9;
export const IMBISS_VON = 69;
export const IMBISS_BIS = 72;
/**
 * Dachstuetzen: zwanzig gesperrte Kacheln in den hinteren Reihen des
 * Oberrings. Beim Vorbild stehen die zwanzig schlanken Baumstuetzen des
 * Membrandachs im Oberring und nicht draussen — sie sind der Grund, warum
 * es dort Plaetze mit Sichtbehinderung gibt.
 *
 * Der Bahnabstand 58 ist nicht frei gewaehlt: dort liegt die Sitzreihe auf
 * 24,5 m, und bis zum Untergurt des Daches auf 33,0 m bleiben genau die
 * 8,50 m Stuetzenlaenge des Vorbilds.
 *
 * Die Winkel stehen so, dass der Ring gleichmaessig getragen wird — beim
 * Vorbild 32 bis 40 m Abstand — und keine Stuetze auf einer Torachse
 * (0, 34, 68 Grad) oder einer Oberring-Treppenachse (17, 51, 85 Grad)
 * landet. Am naechsten kommt sich das bei 53,5 gegen 51 Grad, und das sind
 * immer noch knapp vier Meter. Acht Stuetzen je Kurve, zwei je Gerade.
 */
export const STUETZEN_GERADE_X = [20];
export const STUETZEN_WINKEL_GRAD = [8.5, 31, 53.5, 76];
export const STUETZE_D = 58;
/** Tile-IDs — kurz, weil 80 000 Kacheln als JSON ueber die Leitung gehen. */
export const KACHEL = {
    spielfeld: 'spielfeld',
    rasen: 'rasen',
    tartan: 'tartan',
    beton: 'beton',
    umgang: 'umgang',
    /** Der Rundgang oben im Unterring — begehbar, wie der Umgang, aber eigene Optik. */
    rundgang: 'rundgang',
    /** Die Wand des Pfeilerkranzes zwischen Rundgang und Promenade. */
    pfeiler: 'pfeiler',
    stufe: 'stufe',
    treppe: 'treppe',
    marathontor: 'marathontor',
    promenade: 'promenade',
    sockel: 'sockel',
    vorplatz: 'vorplatz',
    mauer: 'mauer',
    podest: 'podest',
    fassade: 'fassade',
    stuetze: 'stuetze',
    tor: 'tor',
    imbiss: 'imbiss',
    bruestung: 'bruestung',
    /**
     * Die Bruestung an der Vorderkante des Oberrings. Eigene Kachel, weil das
     * Theme sie anders baut als ein Gelaender: unten eine massive Mauer, darauf
     * eine Scheibe. An der Mauer haengen beim Vorbild die Zaunfahnen, und dort
     * kommen spaeter die Banner hin.
     */
    bande: 'bande',
    sitz: 'sitz',
};
export const STADION_MAP_ID = 'stadion';
export const STADION_THEME = 'stadion';
//# sourceMappingURL=masse.js.map